import {dollarsToCpmMicros, mediationLineDisplayNameForSubmit} from '../mediationDraft.ts';
import type {DryRunStep, MissingApiNote} from '../mediationDraft.ts';
import {
    ADMOB_NETWORK_WATERFALL_SOURCE_ID,
    buildOpmcCreateChange,
    buildOpmcMappingChange,
} from '../opmcWaterfall.ts';
import type {BuildConfirmStepsContext, BuildConfirmStepsResult, WaterfallSourceAdapter} from './types.ts';

function mappingPathFor(ctx: BuildConfirmStepsContext): string {
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    return mappingResourceId
        ? `/admob/accounts/${ctx.accountId}/ad-units/${mappingResourceId}/mappings`
        : `/admob/accounts/${ctx.accountId}/ad-units/{standard_ad_unit_resource_id}/mappings`;
}

function buildAddWaterfallLineStep(
    ctx: BuildConfirmStepsContext,
    opts: {
        stepId: string;
        dependsOn: string;
        displayName: string;
        notes: string[];
        tempId: string;
        micros: string | null;
    },
): DryRunStep {
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    const lineBody: Record<string, any> = {
        id: opts.tempId,
        displayName: opts.displayName,
        adSourceId: ADMOB_NETWORK_WATERFALL_SOURCE_ID,
        cpmMode: 'MANUAL',
        state: 'ENABLED',
    };
    if (opts.micros != null) {
        lineBody.cpmMicros = opts.micros;
    }
    return {
        id: opts.stepId,
        kind: 'add-waterfall-line',
        label: `绑定瀑布流行 ${opts.displayName}`,
        method: 'PATCH',
        path: ctx.patchPath,
        change: {
            updateMask: `mediationGroupLines["${opts.tempId}"]`,
            mediationGroupLines: {[opts.tempId]: lineBody},
        },
        notes: opts.notes,
        dependsOn: opts.dependsOn,
        rowKey: ctx.row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    };
}

export function buildConfirmSteps(ctx: BuildConfirmStepsContext): BuildConfirmStepsResult {
    const steps: DryRunStep[] = [];
    const missingApis: MissingApiNote[] = [];
    const warnings: string[] = [];
    const row = ctx.row;

    if (!ctx.appId) {
        missingApis.push({
            id: `no-app-id-${row.key}`,
            message: `新建行「${row.displayName || row.key}」缺少 appId，无法 dry-run 创建 OPMC。`,
        });
        return {steps, missingApis, warnings};
    }
    if (!ctx.standardAdUnitId) {
        missingApis.push({
            id: `no-targeting-ad-unit-${row.key}`,
            message: `新建行「${row.displayName || row.key}」缺少中介组 targeting.adUnitIds[0]（标准广告单元），无法串联 OPMC → mapping → 瀑布流行。`,
        });
        return {steps, missingApis, warnings};
    }

    const bindMode = row.opmcBindMode === 'existing' ? 'existing' : 'create';
    const selectedOpmcId = String(row.selectedOpmcId || '').trim();
    const mappingResourceId = String(ctx.standardAdUnitResourceId || '').trim();
    const displayName = row.displayName || 'waterfall_line';
    const lineDisplayName = mediationLineDisplayNameForSubmit(
        row,
        ctx.draftRows || [],
        ctx.lineLabelHint || 'AdMob',
    );
    const micros = dollarsToCpmMicros(row.floorPrice);
    const mappingPath = mappingPathFor(ctx);

    if (bindMode === 'existing') {
        if (!selectedOpmcId) {
            missingApis.push({
                id: `no-opmc-id-${row.key}`,
                message: `新建行「${row.displayName || row.key}」选择了已有 OPMC，但未选择具体 OPMC。`,
            });
            return {steps, missingApis, warnings};
        }
        const mappingStepId = `create-mapping-${row.key}`;
        const addLineStepId = `add-waterfall-line-${row.key}`;
        const tempId = ctx.allocTempLineId();
        const notes = [
            '选已有 OPMC：跳过创建 OPMC；Apply 时 create-mapping → 绑定 MANUAL 行。',
        ];
        steps.push({
            id: mappingStepId,
            kind: 'create-mapping',
            label: `创建 mapping ${displayName}`,
            method: 'POST',
            path: mappingPath,
            change: buildOpmcMappingChange({
                opmcId: selectedOpmcId,
                displayName,
                platform: ctx.platform,
                format: ctx.adFormat,
            }),
            notes: [...notes],
            rowKey: row.key,
            standardAdUnitId: ctx.standardAdUnitId,
            standardAdUnitResourceId: mappingResourceId || undefined,
        });
        steps.push(
            buildAddWaterfallLineStep(ctx, {
                stepId: addLineStepId,
                dependsOn: mappingStepId,
                displayName: lineDisplayName,
                notes: [...notes, 'dependsOn create-mapping'],
                tempId,
                micros,
            }),
        );
        return {steps, missingApis, warnings};
    }

    const notes: string[] = [
        'Apply 时串联：创建 OPMC → 创建 mapping → PATCH 绑定 MANUAL 瀑布流行；有 dependsOn 的步骤在 Apply 循环内现场 dry-run。',
    ];
    const opmcStepId = `create-opmc-${row.key}`;
    const mappingStepId = `create-mapping-${row.key}`;
    const addLineStepId = `add-waterfall-line-${row.key}`;
    const tempId = ctx.allocTempLineId();
    const createOpmcPath = `/admob/accounts/${ctx.accountId}/admob-network-waterfall-ad-units`;

    steps.push({
        id: opmcStepId,
        kind: 'create-opmc',
        label: `创建 OPMC ${displayName}`,
        method: 'POST',
        path: createOpmcPath,
        change: buildOpmcCreateChange({
            appId: ctx.appId,
            displayName,
            format: ctx.adFormat || 'BANNER',
            floorDollars: row.floorPrice,
            adTypes: ctx.adTypes?.length ? ctx.adTypes : ['RICH_MEDIA'],
        }),
        notes: [...notes],
        rowKey: row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    });

    if (!mappingResourceId) {
        notes.push(
            'standardAdUnitResourceId 在计划阶段未知；Apply 前需解析标准广告单元 name 末段填入 mapping 路径。',
        );
    }
    steps.push({
        id: mappingStepId,
        kind: 'create-mapping',
        label: `创建 mapping ${displayName}`,
        method: 'POST',
        path: mappingPath,
        // Placeholder until Apply fills OPMC id from dependsOn result.
        change: {},
        notes: [...notes, 'dependsOn create-opmc；预览 dry-run 不提交占位 change。'],
        dependsOn: opmcStepId,
        rowKey: row.key,
        standardAdUnitId: ctx.standardAdUnitId,
        standardAdUnitResourceId: mappingResourceId || undefined,
    });

    steps.push(
        buildAddWaterfallLineStep(ctx, {
            stepId: addLineStepId,
            dependsOn: mappingStepId,
            displayName: lineDisplayName,
            notes: [...notes, 'dependsOn create-mapping；adUnitMappings 在 Apply 时由 mapping.name 回填。'],
            tempId,
            micros,
        }),
    );

    return {steps, missingApis, warnings};
}

export const admobOpmcAdapter: WaterfallSourceAdapter = {
    sourceId: ADMOB_NETWORK_WATERFALL_SOURCE_ID,
    displayNameHint: 'AdMob Network Waterfall',
    supportsCreate: true,
    supportsPickExisting: true,
    hasAccountApi: false,
    buildConfirmSteps,
};
